import AddIcon from '@mui/icons-material/Add';
import { LoadingButton } from '@mui/lab';
import {
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import { Practitioner } from 'fhir/r4b';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import { FC, useMemo, useState } from 'react';
import { CompleteConfiguration } from 'src/components/CompleteConfiguration';
import { MappedStatusChip } from 'src/components/MappedStatusChip';
import { PageTitle } from 'src/features/visits/shared/components/PageTitle';
import { useGetErxConfigQuery } from 'src/features/visits/telemed/hooks/useGetErxConfig';
import { useApiClients } from 'src/hooks/useAppClients';
import useEvolveUser from 'src/hooks/useEvolveUser';
import { RoleType } from 'utils/lib/types/api/user.types';
import { formatDateToMDYWithTime } from 'utils/lib/utils/date';
import { RoundedButton } from '../../../../../components/RoundedButton';
import { useChartSection } from '../../hooks/useChartSection';
import { useGetAppointmentAccessibility } from '../../hooks/useGetAppointmentAccessibility';
import { useAppointmentData } from '../../stores/appointment/appointment.store';
import { ERX, ERXStatus } from '../ERX';
import { PreferredPharmacy } from '../PreferredPharmacy';

const getPractitionerName = (practitioner?: Practitioner): string | undefined => {
  if (!practitioner) {
    return;
  }
  const givenName = practitioner?.name?.[0]?.given?.at(0) ?? '';
  const familyName = practitioner?.name?.[0]?.family ?? '';
  return `${familyName}, ${givenName}`.trim();
};

export const medicationStatusMapper = {
  loading: {
    background: {
      primary: '#B3E5FC',
    },
    color: {
      primary: '#01579B',
    },
  },
  active: {
    background: {
      primary: '#B3E5FC',
    },
    color: {
      primary: '#01579B',
    },
  },
  'on-hold': {
    background: {
      primary: '#D1C4E9',
    },
    color: {
      primary: '#311B92',
    },
  },
  cancelled: {
    background: {
      primary: '#FFFFFF',
    },
    color: {
      primary: '#616161',
    },
  },
  completed: {
    background: {
      primary: '#C8E6C9',
    },
    color: {
      primary: '#1B5E20',
    },
  },
  'entered-in-error': {
    background: {
      primary: '#FFE0B2',
    },
    color: {
      primary: '#E65100',
    },
  },
  stopped: {
    background: {
      primary: '#FFCCBC',
    },
    color: {
      primary: '#BF360C',
    },
  },
  draft: {
    background: {
      primary: '#FFFFFF',
    },
    color: {
      primary: '#616161',
    },
  },
  unknown: {
    background: {
      primary: '#FFFFFF',
    },
    color: {
      primary: '#616161',
    },
  },
};

interface ERxContainerProps {
  showHeader?: boolean;
}

export const ERxContainer: FC<ERxContainerProps> = ({ showHeader = true }) => {
  const { appointment, patient } = useAppointmentData();
  const userTimezone = DateTime.local().zoneName;
  const appointmentStart = useMemo(
    () => formatDateToMDYWithTime(appointment?.start, userTimezone),
    [appointment?.start, userTimezone]
  );
  const { isAppointmentReadOnly: isReadOnly } = useGetAppointmentAccessibility();

  const { isLoading, isFetching, refetch, data: chartFields } = useChartSection('plan', { refetchInterval: 10000 });

  const [isERXOpen, setIsERXOpen] = useState(false);
  const [erxStatus, setERXStatus] = useState(ERXStatus.INITIAL);
  const [openTooltip, setOpenTooltip] = useState(false);
  const [cancellationLoading, setCancellationLoading] = useState<string[]>([]);
  const { oystehr } = useApiClients();
  const user = useEvolveUser();
  const { data: erxConfigData, isLoading: isErxConfigLoading } = useGetErxConfigQuery();

  const cancelPrescription = async (medRequestId: string, patientId: string): Promise<void> => {
    if (!oystehr) {
      enqueueSnackbar('An error occurred. Please try again.', { variant: 'error' });
      return;
    }
    setCancellationLoading((prevState) => [...prevState, medRequestId]);
    try {
      await oystehr.erx.cancelPrescription({ medicationRequestId: medRequestId, patientId });
    } catch (error) {
      enqueueSnackbar('An error occurred while cancelling prescription. Please try again.', { variant: 'error' });
      console.error(`Error cancelling prescription: ${error}`);
    } finally {
      await refetch();
      setCancellationLoading((prevState) => prevState.filter((item) => item !== medRequestId));
    }
  };

  const handleCloseTooltip = (): void => {
    setOpenTooltip(false);
  };

  const handleOpenTooltip = (): void => {
    setOpenTooltip(true);
  };

  const handleSetup = (): void => {
    window.open('https://docs.oystehr.com/ottehr/setup/prescriptions/', '_blank');
  };

  const onNewOrderClick = async (): Promise<void> => {
    // await oystehr?.erx.unenrollPractitioner({ practitionerId: user!.profileResource!.id! });
    setIsERXOpen(true);
  };

  return (
    <>
      <Stack gap={1}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" gap={1} alignItems="center">
            {showHeader && <PageTitle label="eRX" showIntakeNotesButton={false} />}
            {(isLoading || isFetching || cancellationLoading.length > 0) && <CircularProgress size={16} />}
          </Stack>
          <Tooltip
            placement="top"
            title="You don't have the necessary role to access ERX. Please contact your administrator."
            open={openTooltip && !isReadOnly && !user?.hasRole([RoleType.Provider])}
            onClose={handleCloseTooltip}
            onOpen={handleOpenTooltip}
          >
            <Stack>
              {isERXOpen && erxStatus !== ERXStatus.LOADING ? (
                <RoundedButton
                  disabled={isReadOnly || !user?.hasRole([RoleType.Provider])}
                  variant="contained"
                  onClick={() => {
                    setIsERXOpen(false);
                  }}
                >
                  Close eRX
                </RoundedButton>
              ) : (
                <RoundedButton
                  disabled={
                    isReadOnly ||
                    erxStatus === ERXStatus.LOADING ||
                    !user?.hasRole([RoleType.Provider]) ||
                    !erxConfigData?.configured
                  }
                  variant="contained"
                  onClick={() => onNewOrderClick()}
                  startIcon={erxStatus === ERXStatus.LOADING ? <CircularProgress size={16} /> : <AddIcon />}
                >
                  {erxStatus === ERXStatus.LOADING ? 'Loading eRx' : 'Open eRx'}
                </RoundedButton>
              )}
            </Stack>
          </Tooltip>
        </Stack>

        {chartFields?.preferredPharmacies && <PreferredPharmacy data={chartFields?.preferredPharmacies} />}

        {!erxConfigData?.configured && !isErxConfigLoading && <CompleteConfiguration handleSetup={handleSetup} />}
        {isERXOpen && (
          <ERX
            onStatusChanged={(status) => {
              if (status === ERXStatus.ERROR) {
                setIsERXOpen(false);
              }
              setERXStatus(status);
            }}
            showDefaultAlert={true}
          />
        )}
        <div id="prescribe-dialog" style={{ flex: '1 0 auto', display: 'flex' }} />

        {chartFields?.prescribedMedications && chartFields.prescribedMedications.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead
                sx={{
                  '& .MuiTableCell-head': {
                    fontWeight: 700,
                  },
                }}
              >
                <TableRow>
                  <TableCell>Medication</TableCell>
                  <TableCell>Patient instructions (SIG)</TableCell>
                  {/*<TableCell>Dx</TableCell>*/}
                  <TableCell>Visit</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Order added</TableCell>
                  {/*<TableCell>Pharmacy</TableCell>*/}
                  <TableCell>Status</TableCell>
                  {!isReadOnly && <TableCell>Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {chartFields.prescribedMedications.map((row) => {
                  const rowAdded = formatDateToMDYWithTime(row?.added, userTimezone);
                  return (
                    <TableRow key={row.resourceId}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body2">{row.name}</Typography>
                          {row.isRenewal && <Chip label="Refill" size="small" color="primary" variant="outlined" />}
                        </Stack>
                      </TableCell>
                      <TableCell>{row.instructions}</TableCell>
                      {/*<TableCell>Dx</TableCell>*/}
                      <TableCell>
                        {appointmentStart && (
                          <>
                            <Typography variant="body2">{appointmentStart.date}</Typography>
                            <Typography variant="body2">{appointmentStart.time}</Typography>
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        {getPractitionerName(
                          chartFields.prescribedMedicationsRequesterPractitioners?.find(
                            (practitioner) => practitioner.id === row.provider
                          )
                        )}
                      </TableCell>
                      <TableCell>
                        {rowAdded && (
                          <>
                            <Typography variant="body2">{rowAdded.date}</Typography>
                            <Typography variant="body2">{rowAdded.time}</Typography>
                          </>
                        )}
                      </TableCell>
                      {/*<TableCell>Pharmacy</TableCell>*/}
                      <TableCell>
                        {!!row.status && <MappedStatusChip status={row.status} mapper={medicationStatusMapper} />}
                      </TableCell>
                      {!isReadOnly && patient?.id && (
                        <TableCell>
                          <LoadingButton
                            loading={cancellationLoading.includes(row.resourceId!)}
                            variant="text"
                            color="error"
                            onClick={() => cancelPrescription(row.resourceId!, patient.id!)}
                            disabled={
                              row.status === 'loading' ||
                              row.status === 'completed' ||
                              row.status === 'cancelled' ||
                              cancellationLoading.includes(row.resourceId!)
                            }
                          >
                            Cancel
                          </LoadingButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </>
  );
};
